import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import {
    ErrorMessage,
    StyledBagAnimation,
    StyledCodeEnteringContainer,
    StyledContainer,
    StyledContent,
    StyledForm,
    StyledInput,
    StyledInputForm,
    StyledText
} from './styles'
import { colors } from 'shared/assets/styles'
import bag from 'app/tu-preferes-king/assets/tpk-2024-tuto-bag.gif'
import { Button } from 'app-v2/design-systems/components/inputs/button'
import { useHistory } from 'react-router-dom'
import { isMobile, isTablet } from 'react-device-detect'
import { useLocation } from 'react-router-dom'
import { Errors, resetState, tryCode } from 'shared/store/tu-preferes-king'
import { QrButton } from '../qr-button/QrButton'
import { Recaptcha } from '../../../../common/recaptcha'
import { QRCodeScannerModal } from '../../../../common/qr-code-scanner/QRCodeScannerModal'
import { RecaptchaV2 } from '../../../../common/recaptcha/RecaptchaV2'
import { Spinner } from '../../../../assets/icons'
import { useDispatch, useSelector } from 'react-redux'
import { TPKDone } from './TPKDone'
import { SPACING, BORDER } from 'app-v2/design-tokens'
import styled from 'styled-components'
import { media } from 'app/assets/styles'

const isDesktop = !(isMobile || isTablet)

export function CodeCheck({
    disable,
    captchaMode
}: {
    disable: boolean,
    captchaMode: 'V2' | 'V3'
}) {
    const intl = useIntl()
    const history = useHistory()
    const { search, pathname } = useLocation()
    const [error, setError] = useState()
    const [code, setCode] = useState('')
    const [scannerOpen, setScannerOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const recaptchaResolve = useRef()
    const recaptchaPromise = useRef()
    const recaptchaRef = useRef()
    const inputRef = useRef()
    const dispatch = useDispatch()

    const initCaptchaPromise = useCallback(() => {
        if (captchaMode === 'V3' && !disable) {
            recaptchaPromise.current = new Promise(r => {
                recaptchaResolve.current = r
            })
        }
    }, [disable, captchaMode])

    useEffect(() => {
        if (!recaptchaPromise.current) {
            initCaptchaPromise()
        }
    }, [initCaptchaPromise])

    const getCaptchaValue = useCallback(async () => {
        if (captchaMode === 'V3') {
            initCaptchaPromise()
            recaptchaRef.current.execute()
            return await recaptchaPromise.current
        } else {
            const token = await recaptchaRef.current
                .executeAsync()
                .catch(err => {
                    return ''
                })
                .finally(() => recaptchaRef.current.reset())
            return token
        }
    }, [initCaptchaPromise])

    const submitCode = useCallback(
        async (code: string) => {
            if (loading) {
                return
            }
            dispatch(resetState())
            setLoading(true)
            const recaptchaToken = await getCaptchaValue()
            try {
                const response = await tryCode(code, recaptchaToken)
                history.push(
                    intl.formatMessage(
                        {
                            id: 'routes.tupreferesking.duel.tpkToken'
                        },
                        { tpkToken: response.tpkToken }
                    )
                )
            } catch (e) {
                throw new Error(
                    intl.formatMessage({
                        id: `tupreferesking.errors.${e.message}`
                    })
                )
            } finally {
                setLoading(false)
            }
        },
        [intl, history, getCaptchaValue, loading]
    )

    const submitCodeFromQRScanner = useCallback(
        async (code: string | null) => {
            if (!code) {
                throw new Error(
                    intl.formatMessage({
                        id: `tupreferesking.errors.${Errors.BAD_QRCODE}`
                    })
                )
            }
            return submitCode(code)
        },
        [submitCode]
    )

    const submitCodeFromInput = useCallback(
        async (code: string) => {
            setError('')
            try {
                await submitCode(code)
            } catch (e) {
                setError(e.message)
                inputRef.current && inputRef.current.focus()
            }
        },
        [submitCode]
    )

    useEffect(() => {
        if (disable) {
            return
        }

        const url = new URLSearchParams(search)

        if (url.has('code')) {
            const code = url.get('code')
            url.delete('code')
            setCode(code)
            history.replace({
                pathname,
                search: '?' + url.toString()
            })

            submitCodeFromInput(code)
        }
    }, [history, search, pathname, disable, submitCodeFromInput])

    if (disable) {
        return (
            <StyledDoneContainer>
                <TPKDone />
            </StyledDoneContainer>
        )
    }

    const mobileConfirm =
        code &&
        (!error ||
            [Errors.INVALID_CAPTCHA, Errors.INTERNAL_ERROR].includes(error))

    return (
        <StyledForm
            onSubmit={e => {
                e.preventDefault()
                submitCodeFromInput(code)
            }}>
            {captchaMode === 'V3' && (
                <Recaptcha
                    ref={recaptchaRef}
                    verifyCallback={token => recaptchaResolve.current(token)}
                    action={'TPKCodeInput'}
                />
            )}
            {captchaMode === 'V2' && <RecaptchaV2 ref={recaptchaRef} />}
            <StyledContainer>
                <StyledBagAnimation src={bag} />
                <StyledContent>
                    <StyledText color={colors.bbqBrown}>
                        {intl.formatMessage({
                            id: isDesktop
                                ? 'tupreferesking.home.entercode'
                                : 'tupreferesking.home.enterOrScanCode'
                        })}
                    </StyledText>
                    <StyledCodeEnteringContainer>
                        <StyledInputForm
                            error={!!error}
                            paddingRight={!isDesktop}>
                            <StyledInput
                                ref={inputRef}
                                value={code}
                                type="text"
                                placeholder={intl.formatMessage({
                                    id: 'tupreferesking.home.code.placeholder'
                                })}
                                onChange={e => {
                                    setError('')
                                    setCode(e.target.value.toUpperCase())
                                }}
                            />
                            {!isDesktop && (
                                <QrButton
                                    loading={loading}
                                    mode={mobileConfirm ? 'send' : 'qr'}
                                    onQrClick={() => setScannerOpen(true)}
                                />
                            )}
                        </StyledInputForm>
                        {isDesktop && (
                            <Button
                                disabled={loading}
                                buttonColor={colors.crunchyGreen}
                                labelColor={colors.white}
                                otherStyle={{
                                    padding: '0 24px',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}>
                                {intl.formatMessage({ id: 'confirm' })}
                                {loading && (
                                    <Spinner size={18} color={colors.white} />
                                )}
                            </Button>
                        )}
                    </StyledCodeEnteringContainer>
                    <ErrorMessage error={!!error}>{error}</ErrorMessage>
                </StyledContent>
            </StyledContainer>
            <QRCodeScannerModal
                open={scannerOpen}
                onClose={() => setScannerOpen(false)}
                onScan={async (result: string) => {
                    const parsed = new URL(result)
                    const code = parsed.searchParams.get('code')
                    await submitCodeFromQRScanner(code)
                }}
            />
        </StyledForm>
    )
}

const StyledDoneContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: ${SPACING.M};
    border-radius: ${BORDER.RADIUS.XL};
    background-color: ${colors.white};

    ${media.medium`
        margin: 56px;
        padding: ${SPACING.XXXL};
    `}
`
